import { createTheme } from '@mui/material/styles';

export const memerTheme = createTheme({
    palette: {
        primary: {
            main: '#D4AF37',        // MEMER Gold
            dark: '#C19B2C',        // Gold Dark
            light: '#FFB800',       // Gold Hover
            contrastText: '#1A1A1A', // Dark text on gold
        },
        secondary: {
            main: '#2E7D32',        // MEMER Green
            light: '#4caf50',       // Green Light
            dark: '#1B5E20',        // Green Dark
            contrastText: '#ffffff',
        },
        background: {
            default: '#FAFAFA',     // MEMER bg-light
            paper: '#FFFFFF',       // MEMER bg-white
        },
        text: {
            primary: '#1A1A1A',     // MEMER dark text
            secondary: '#6B6B6B',   // MEMER light text
        },
        info: {
            main: '#D4AF37',        // Use gold for info
            contrastText: '#1A1A1A',
        },
        success: {
            main: '#2E7D32',        // MEMER green
            contrastText: '#ffffff',
        },
        warning: {
            main: '#FFB800',        // Gold warning
            contrastText: '#1A1A1A',
        },
        error: {
            main: '#ef4444',        // Keep standard red
            contrastText: '#ffffff',
        },
    },
    typography: {
        fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
        h1: {
            fontFamily: '"Noto Serif SC", serif',
            fontWeight: 700,
            color: '#1A1A1A',
            fontSize: '3.5rem',
            lineHeight: 1.2,
            '@media (max-width:600px)': {
                fontSize: '2.5rem',
            },
        },
        h2: {
            fontFamily: '"Noto Serif SC", serif',
            fontWeight: 700,
            color: '#1A1A1A',
            fontSize: '2.5rem',
            lineHeight: 1.3,
            '@media (max-width:600px)': {
                fontSize: '2rem',
            },
        },
        h3: {
            fontFamily: '"Noto Serif SC", serif',
            fontWeight: 700,
            color: '#1A1A1A',
            fontSize: '2rem',
            lineHeight: 1.4,
            '@media (max-width:600px)': {
                fontSize: '1.5rem',
            },
        },
        h4: {
            fontFamily: '"Noto Serif SC", serif',
            fontWeight: 700,
            color: '#1A1A1A',
            fontSize: '1.5rem',
            lineHeight: 1.4,
        },
        h5: {
            fontFamily: '"Noto Serif SC", serif',
            fontWeight: 600,
            color: '#1A1A1A',
            fontSize: '1.25rem',
            lineHeight: 1.5,
        },
        h6: {
            fontFamily: '"Noto Serif SC", serif',
            fontWeight: 600,
            color: '#1A1A1A',
            fontSize: '1rem',
            lineHeight: 1.5,
        },
        body1: {
            fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
            fontSize: '1rem',
            lineHeight: 1.6,
            color: '#6B6B6B',
        },
        body2: {
            fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
            fontSize: '0.875rem',
            lineHeight: 1.6,
            color: '#6B6B6B',
        },
        button: {
            fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
            fontWeight: 600,
            textTransform: 'none',
        },
    },
    components: {
        MuiButton: {
            styleOverrides: {
                root: {
                    borderRadius: '8px',
                    textTransform: 'none',
                    fontWeight: 600,
                    padding: '12px 24px',
                    transition: 'all 0.3s ease',
                    '&:hover': {
                        transform: 'translateY(-1px)',
                        boxShadow: '0 4px 12px rgba(212, 175, 55, 0.3)',
                    },
                },
                containedPrimary: {
                    backgroundColor: '#D4AF37',
                    color: '#1A1A1A',
                    '&:hover': {
                        backgroundColor: '#C19B2C',
                    },
                },
                containedSecondary: {
                    backgroundColor: '#2E7D32',
                    color: '#ffffff',
                    '&:hover': {
                        backgroundColor: '#1B5E20',
                    },
                },
                outlinedPrimary: {
                    borderColor: '#D4AF37',
                    color: '#D4AF37',
                    '&:hover': {
                        backgroundColor: '#D4AF37',
                        color: '#1A1A1A',
                    },
                },
            },
        },
        MuiCard: {
            styleOverrides: {
                root: {
                    borderRadius: '16px',
                    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.08)',
                    transition: 'all 0.3s ease',
                    '&:hover': {
                        boxShadow: '0 12px 32px rgba(0, 0, 0, 0.15)',
                        transform: 'translateY(-2px)',
                    },
                },
            },
        },
        MuiCardHeader: {
            styleOverrides: {
                root: {
                    borderBottom: '1px solid #f0f0f0',
                    paddingBottom: '16px',
                    marginBottom: '16px',
                    position: 'relative',
                    '&::after': {
                        content: '""',
                        position: 'absolute',
                        bottom: '0',
                        left: '16px',
                        width: '80px',
                        height: '4px',
                        background: 'linear-gradient(to right, #D4AF37, #FFB800)',
                        borderRadius: '2px',
                    },
                },
                title: {
                    fontFamily: '"Noto Serif SC", serif',
                    fontWeight: 700,
                    color: '#1A1A1A',
                },
            },
        },
        MuiTextField: {
            styleOverrides: {
                root: {
                    '& .MuiOutlinedInput-root': {
                        borderRadius: '12px',
                        '& fieldset': {
                            borderColor: '#e0e0e0',
                        },
                        '&:hover fieldset': {
                            borderColor: '#D4AF37',
                        },
                        '&.Mui-focused fieldset': {
                            borderColor: '#D4AF37',
                            borderWidth: '2px',
                        },
                    },
                },
            },
        },
        MuiAppBar: {
            styleOverrides: {
                root: {
                    backgroundColor: '#FFFFFF',
                    color: '#1A1A1A',
                    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.08)',
                    borderBottom: '1px solid #f0f0f0',
                },
            },
        },
        MuiContainer: {
            styleOverrides: {
                root: {
                    backgroundColor: '#FAFAFA',
                    minHeight: '100vh',
                },
            },
        },
        MuiChip: {
            styleOverrides: {
                root: {
                    borderRadius: '8px',
                },
                colorPrimary: {
                    backgroundColor: '#D4AF37',
                    color: '#1A1A1A',
                },
                colorSecondary: {
                    backgroundColor: '#2E7D32',
                    color: '#ffffff',
                },
            },
        },
        MuiAlert: {
            styleOverrides: {
                standardInfo: {
                    backgroundColor: '#FFF8E7',
                    color: '#1A1A1A',
                    '& .MuiAlert-icon': {
                        color: '#D4AF37',
                    },
                },
                standardSuccess: {
                    backgroundColor: '#E8F5E8',
                    color: '#1A1A1A',
                    '& .MuiAlert-icon': {
                        color: '#2E7D32',
                    },
                },
            },
        },
    },
});

// Custom theme extensions for MEMER-specific styling
export const memerThemeExtensions = {
    colors: {
        memer: {
            gold: '#D4AF37',
            goldHover: '#FFB800',
            goldDark: '#C19B2C',
            goldLight: '#FFF8E7',
            green: '#2E7D32',
            greenLight: '#4caf50',
            dark: '#1A1A1A',
            medium: '#4A4A4A',
            light: '#6B6B6B',
            gray: '#666666',
            bgLight: '#FAFAFA',
            bgWhite: '#FFFFFF',
        },
    },
    shadows: {
        memerCard: '0 4px 12px rgba(0, 0, 0, 0.08)',
        memerCardHover: '0 12px 32px rgba(0, 0, 0, 0.15)',
        memerGold: '0 12px 20px -10px rgba(212, 175, 55, 0.28), 0 4px 20px 0px rgba(0, 0, 0, 0.12), 0 7px 8px -5px rgba(212, 175, 55, 0.2)',
    },
    spacing: {
        memerXs: '8px',
        memerSm: '16px',
        memerMd: '24px',
        memerLg: '30px',
        memerXl: '40px',
    },
};
